

import React, { useState } from "react";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Typography, CircularProgress } from "@mui/material";
import Card from "@mui/material/Card";
import EntitiesPopup from "layouts/components/EntitiesPopup";

// -----------------------------------------------------------
// 🚀 HELPER FUNCTIONS (Unchanged from last time)
// -----------------------------------------------------------

/**
 * Creates a Map of [date, {rate, isPredicted}] for a profile,
 * using predicted follower counts as a fallback.
 */
const createEngagementRateMapPredicted = (profile) => {
  const map = new Map();
  if (!profile || !profile.data) return map;

  const isYouTube = profile.platform === "YouTube";
  const actualKey = isYouTube ? "subscribers" : "followers";
  const predictedKey = isYouTube ? "predicted_subscribers" : "predicted_followers";

  profile.data.forEach((item) => {
    let totalEngagement = 0;
    let usedPredicted = false;
    let totalFollowers = 0;

    if (item.posts && item.posts.length > 0) {
      totalEngagement += item.posts.reduce(
        (acc, post) =>
          acc +
          (post.postMetrics?.likes || post.postMetrics?.like || 0) +
          (post.postMetrics?.comments || 0) +
          (post.postMetrics?.shares || 0) +
          (post.postMetrics?.love || 0) +
          (post.postMetrics?.wow || 0) +
          (post.postMetrics?.haha || 0) +
          (post.postMetrics?.sad || 0) +
          (post.postMetrics?.angry || 0),
        0
      );
    }
    if (item.videos && item.videos.length > 0) {
      totalEngagement += item.videos.reduce(
        (acc, video) =>
          acc +
          (video.videoMetrics?.likeCount || 0) +
          (video.videoMetrics?.commentCount || 0),
        0
      );
    }

    if (item[actualKey] && item[actualKey] > 0) {
      totalFollowers = item[actualKey];
    } else if (item[predictedKey] && item[predictedKey] > 0) {
      totalFollowers = item[predictedKey];
      usedPredicted = true;
    }

    const rate = totalFollowers > 0 ? (totalEngagement / totalFollowers) * 100 : null;
    map.set(item.date, { rate, isPredicted: usedPredicted });
  });
  return map;
};

/**
 * Creates a Map of [date, rate] for a profile,
 * without using any predicted follower counts.
 */
const createEngagementRateMap = (profile) => {
  const map = new Map();
  if (!profile || !profile.data) return map;

  profile.data.forEach((item) => {
    let totalEngagement = 0;
    const totalFollowers = item.followers || item.subscribers || 0;

    if (item.posts && item.posts.length > 0) {
      totalEngagement += item.posts.reduce(
        (acc, post) =>
          acc +
          (post.postMetrics?.likes || post.postMetrics?.like || 0) +
          (post.postMetrics?.comments || 0) +
          (post.postMetrics?.shares || 0) +
          (post.postMetrics?.love || 0) +
          (post.postMetrics?.wow || 0) +
          (post.postMetrics?.haha || 0) +
          (post.postMetrics?.sad || 0) +
          (post.postMetrics?.angry || 0),
        0
      );
    }
    if (item.videos && item.videos.length > 0) {
      totalEngagement += item.videos.reduce(
        (acc, video) =>
          acc +
          (video.videoMetrics?.likeCount || 0) +
          (video.videoMetrics?.commentCount || 0),
        0
      );
    }

    const rate = totalFollowers > 0 ? (totalEngagement / totalFollowers) * 100 : null;
    map.set(item.date, rate);
  });
  return map;
};

// -----------------------------------------------------------
// 🚀 MAIN COMPONENT
// -----------------------------------------------------------

function AudienceEngagementRateChart({
  title,
  datasets,
  previousdatasets,
  futuredatasets = [],
  isPredictionEnabled = false,
  isLoading = false,
  entitlements, // ✅ Added prop
  viewMode, // ✅ NEW
  onViewModeChange // ✅ NEW
}) {
  // ✅ Popup state (unchanged)
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupDate, setPopupDate] = useState(null);

  const handlePointClick = (date) => {
    setPopupDate(date);
    setIsPopupOpen(true);
  };

  const handlePopupClose = () => {
    setIsPopupOpen(false);
    setPopupDate(null);
  };

  // ⏳ Loader during fetch (unchanged)
  if (isLoading) {
    return (
      <Card>
        <MDBox p={3} display="flex" justifyContent="center" alignItems="center" height="250px">
          <CircularProgress size={32} thickness={4} color="accent" />
        </MDBox>
      </Card>
    );
  }

  // (No data check is unchanged)
  if (!datasets) {
    return (
      <Card>
        <MDBox p={3} textAlign="center">
          <MDTypography variant="body2" color="text">
            No engagement rate data available.
          </MDTypography>
        </MDBox>
      </Card>
    );
  }

  // ⬇️=========================================
  // ⬇️ DATA & LABEL ALIGNMENT (Unchanged)
  // ⬇️=========================================

  // ✅ 1. Prepare future data
  let futureData = isPredictionEnabled && futuredatasets ? futuredatasets : [];

  // ✅ 2. Create master list of all unique dates
  const allActualDates = new Set();
  datasets.forEach(profile => profile.data.forEach(item => allActualDates.add(item.date)));

  const allFutureDates = new Set();
  if (isPredictionEnabled && futureData.length > 0) {
    futureData.forEach(profile => profile.data.forEach(item => allFutureDates.add(item.date)));
  }

  // ✅ 3. Combine, sort, and create final X-axis labels
  const actualLabels = Array.from(allActualDates).sort((a, b) => new Date(a) - new Date(b));
  const predictedLabels = Array.from(allFutureDates)
    .sort((a, b) => new Date(a) - new Date(b))
    .filter(date => !allActualDates.has(date));

  const chartLabels = [...actualLabels, ...predictedLabels];

  // ✅ 4. Align previous datasets
  const updatedPreviousDatasets = isPredictionEnabled
    ? [...previousdatasets, ...datasets]
    : previousdatasets;

  // ✅ 5. Construct chart data, mapping against master `chartLabels`
  const chartData = {
    labels: chartLabels,
    datasets: [
      ...datasets.map((profile) => {
        const rateMap = createEngagementRateMapPredicted(profile);
        return {
          label: profile.name || profile.label,
          // ⬇️ FIXED: Use 0 for missing dates, but keep `null` if it exists
          data: chartLabels.map(date => {
            const point = rateMap.get(date);
            // If date is not in the map, return 0.
            // Otherwise, return the rate (which could be a number OR null)
            return point === undefined ? 0 : point.rate;
          }),
          isPredictedFlags: chartLabels.map(date => rateMap.get(date)?.isPredicted || false),
          profileId: profile.profileId,
          profilePictureUrl: profile.profilePictureUrl || "",
          platform: profile.platform || "Unknown",
          metricName: "Engagement Rate (%)",
        };
      }),
      ...(isPredictionEnabled
        ? futureData.map((profile) => {
          const rateMap = createEngagementRateMap(profile); // Use the simple map
          return {
            label: `${profile.name || profile.label} (Predicted)`,
            // ⬇️ FIXED: Use 0 for missing dates, but keep `null` if it exists
            data: chartLabels.map(date => {
              const rate = rateMap.get(date);
              // If date is not in the map, return 0.
              // Otherwise, return the rate (which could be a number OR null)
              return rate === undefined ? 0 : rate;
            }),
            borderDash: [5, 5],
            profileId: profile.profileId,
            profilePictureUrl: profile.profilePictureUrl || "",
            platform: profile.platform || "Unknown",
            metricName: "Predicted Engagement Rate (%)",
          };
        })
        : []),
    ],
    previousdatasets: updatedPreviousDatasets.map((profile) => {
      // Sort previous data by date to align roughly by "nth day" with the current chart labels
      const sortedHistory = [...(profile.data || [])].sort((a, b) => new Date(a.date) - new Date(b.date));

      return {
        label: profile.name || profile.label,
        // Map data by INDEX against the chartLabels. 
        // We calculate the rate for the *nth* item in the sorted history corresponding to the *nth* label.
        data: chartLabels.map((_, i) => {
          const item = sortedHistory[i];
          if (!item) return 0;

          // Helper logic inlined or reused (since helpers are outside scope, we reconstruct rate calc here slightly or reuse createEngagementRateMap principle)
          // Actually, createEngagementRateMap returns a Map. 
          // Better: Calculate rate on the fly for the item, OR use the helper on the sorted array and access properties.
          // Since createEngagementRateMap works on profile, let's just calculate it directly for this item.

          let totalEngagement = 0;
          const totalFollowers = item.followers || item.subscribers || 0;

          if (item.posts && item.posts.length > 0) {
            totalEngagement += item.posts.reduce(
              (acc, post) =>
                acc +
                (post.postMetrics?.likes || post.postMetrics?.like || 0) +
                (post.postMetrics?.comments || 0) +
                (post.postMetrics?.shares || 0) +
                (post.postMetrics?.love || 0) +
                (post.postMetrics?.wow || 0) +
                (post.postMetrics?.haha || 0) +
                (post.postMetrics?.sad || 0) +
                (post.postMetrics?.angry || 0),
              0
            );
          }
          if (item.videos && item.videos.length > 0) {
            totalEngagement += item.videos.reduce(
              (acc, video) =>
                acc +
                (video.videoMetrics?.likeCount || 0) +
                (video.videoMetrics?.commentCount || 0),
              0
            );
          }

          const rate = totalFollowers > 0 ? (totalEngagement / totalFollowers) * 100 : 0;
          return rate;
        }),
        profileId: profile.profileId,
        profilePictureUrl: profile.profilePictureUrl || "",
        platform: profile.platform || "Unknown",
        metricName: "Engagement Rate (%)",
      };
    }),
  };

  // ⬆️=========================================
  // ⬆️ END OF FIXED LOGIC
  // ⬆️=========================================

  // ✅ Tooltip content (unchanged)
  // ✅ Tooltip content
  const tooltipContent = (
    <div style={{ maxWidth: "350px", textAlign: "left" }}>
      <Typography variant="body2" gutterBottom>
        <strong>Description:</strong>
      </Typography>
      <Typography variant="caption" display="block" gutterBottom>
        This chart measures how engaged your audience is over time. It calculates the
        engagement rate by comparing total interactions (likes, comments, shares, etc.) to the total
        audience size.
      </Typography>

      <Typography variant="body2" gutterBottom>
        <strong>How to read the chart:</strong>
      </Typography>
      <Typography variant="caption" display="block" gutterBottom>
        - <strong>Higher values</strong> indicate a more engaged audience. <br />
        - <strong>A declining trend</strong> may suggest reduced audience interaction. <br />
        - <strong>Dotted lines</strong> indicate estimates using predicted audience counts.
      </Typography>

      <Typography variant="body2" gutterBottom>
        <strong>Insights you can gain:</strong>
      </Typography>
      <Typography variant="caption" display="block">
        - Identify trends in audience engagement over time. <br />
        - Compare engagement across different profiles or platforms. <br />
        - Detect when engagement increases due to specific content strategies.
      </Typography>
    </div>
  );

  // ✅ Render chart + popup (unchanged)
  return (
    <>
      <DefaultLineChart
        title={title}
        description={tooltipContent}
        height="19.125rem"
        chart={chartData}
        aggregate={true}
        rate={true}
        is_derived={true}
        onPointClick={handlePointClick}
        rawData={datasets}
        entitlements={entitlements} // ✅ Pass entitlements
        viewMode={viewMode} // ✅ Pass down CORRECT PROP NAME
        onViewModeChange={onViewModeChange} // ✅ Pass down
      />
      {isPopupOpen && (
        <EntitiesPopup
          isModalOpen={isPopupOpen}
          onClose={handlePopupClose}
          datasets={datasets}
          date={popupDate}
        />
      )}
    </>
  );
}

// 🛡️ PropTypes Validation (unchanged)
AudienceEngagementRateChart.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  datasets: PropTypes.array.isRequired,
  previousdatasets: PropTypes.array.isRequired,
  futuredatasets: PropTypes.array.isRequired,
  isPredictionEnabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  entitlements: PropTypes.object,
  viewMode: PropTypes.string,
  onViewModeChange: PropTypes.func,
};

export default AudienceEngagementRateChart;